import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setColorTheme } from "../store/themeSlice";
import { useUpdateThemeMutation } from "../store/api/custom_ui";
import { useGetThemeQuery } from "../store/api/custom_ui";

function Test() {
  const dispatch = useDispatch();
  const [message, setMessage] = useState("");
  const [updateTheme, { isLoading }] = useUpdateThemeMutation();
  const { data: themeData } = useGetThemeQuery();

  const predefinedThemes = {
    default: {
      primary: "#322D72",
      secondary: "#E98841",
      default: "#667085",
      lightdark: "#333843",
      lightgrey: "#DEDEFA",
      iconintable: "#667085",
      radial1: "rgba(39,11,121,0.3)",
      radial2: "rgba(228,96,44,0.3)",
      radial3: "rgba(228,96,44,0.3)",
      transparent1: "transparent 20%",
      transparent2: "transparent 20%",
      transparent3: "transparent 20%"
    },
    theme1: {
      primary: "#7ED321",
      secondary: "#BD10E0",
      default: "#50E3C2",
      lightdark: "#4A4A4A",
      lightgrey: "#F5A623",
      iconintable: "#D0021B",
      radial1: "rgba(126,211,33,0.3)",
      radial2: "rgba(189,16,224,0.3)",
      radial3: "rgba(189,16,224,0.3)",
      transparent1: "transparent 20%",
      transparent2: "transparent 20%",
      transparent3: "transparent 20%"
    },
    theme2: {
      primary: "#4A90E2",
      secondary: "#F5A623",
      default: "#4A4A4A",
      lightdark: "#2C3E50",
      lightgrey: "#ECF0F1",
      iconintable: "#7F8C8D",
      radial1: "rgba(74,144,226,0.3)",
      radial2: "rgba(245,166,35,0.3)",
      radial3: "rgba(245,166,35,0.3)",
      transparent1: "transparent 20%",
      transparent2: "transparent 20%",
      transparent3: "transparent 20%"
    },
    theme3: {
      primary: "#D0021B",
      secondary: "#F8E71C",
      default: "#BD10E0",
      lightdark: "#4A4A4A",
      lightgrey: "#F5A623",
      iconintable: "#50E3C2",
      radial1: "rgba(208,2,27,0.3)",
      radial2: "rgba(248,231,28,0.3)",
      radial3: "rgba(248,231,28,0.3)",
      transparent1: "transparent 20%",
      transparent2: "transparent 20%",
      transparent3: "transparent 20%"
    },

  };

  const [colors, setColors] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      const theme = JSON.parse(savedTheme);
      const { id, domain, ...rest } = theme;
      return rest;
    }
    return predefinedThemes.default;
  });

  const [selectedTheme, setSelectedTheme] = useState("");
  const [isCustomizing, setIsCustomizing] = useState(false);

  useEffect(() => {
    if (themeData) {
      dispatch(setColorTheme(themeData));
      localStorage.setItem('theme', JSON.stringify(themeData));
      const { id, domain, ...rest } = themeData;
      setColors(rest);
    } else {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        dispatch(setColorTheme(JSON.parse(savedTheme)));
      }
    }
  }, [themeData, dispatch]);

  const handleChangeTheme = async () => {
    setMessage("");
    try {
      await updateTheme(colors).unwrap();
      dispatch(setColorTheme(colors));
      localStorage.setItem('theme', JSON.stringify(colors));
      setMessage("Theme updated successfully!");
      
      // Refresh the page after applying the theme
      setTimeout(() => {
        window.location.reload();
      }, 500); // Small delay to let message appear
    } catch (error) {
      console.error("Error updating theme:", error);
      setMessage("Failed to update theme.");
    }
  };
  

  const handleSelectTheme = (themeKey) => {
    setSelectedTheme(themeKey);
    setColors(predefinedThemes[themeKey]);
    setIsCustomizing(false); // Exit customization mode when selecting a predefined theme
  };

  const handleCustomizeClick = () => {
    setIsCustomizing(true);
    setSelectedTheme("custom"); // Set selected theme to "custom"
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setColors((prevColors) => ({
      ...prevColors,
      [name]: value
    }));
  };

  return (
    <div className="p-6 bg-white rounded-xl w-full shadow-lg mx-auto my-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Theme Customization</h1>
      
      {message && (
        <div className={`p-4 mb-6 rounded-lg ${message.includes("Failed") ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
          {message}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">Predefined Themes</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Object.entries(predefinedThemes).map(([themeKey, themeColors]) => (
            <div
              key={themeKey}
              onClick={() => handleSelectTheme(themeKey)}
              className={`cursor-pointer p-6 rounded-lg border-2 transition-all shadow-sm hover:shadow-md ${
                selectedTheme === themeKey
                  ? "border-primary scale-105 shadow-lg"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <h3 className="text-lg font-medium text-gray-800 mb-4 capitalize">{themeKey}</h3>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(themeColors).map(([colorKey, colorValue]) => (
                  <div
                    key={colorKey}
                    className="h-12 rounded-lg flex items-center justify-center text-xs text-white font-medium"
                    style={{ backgroundColor: colorValue }}
                    title={`${colorKey}: ${colorValue}`}
                  >
                    {colorKey}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <button
          onClick={handleCustomizeClick}
          className="px-6 py-3 rounded-lg bg-gray-200 text-gray-800 font-medium hover:bg-gray-300 transition-all"
        >
          Customize Your Own
        </button>
      </div>

      {isCustomizing && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Customize Colors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(colors).map(([key, value]) => (
              <div key={key} className="flex flex-col space-y-2 bg-gray-50 p-4 rounded-lg">
                <label className="text-sm font-medium text-gray-700 capitalize">
                  {key.replace(/([A-Z])/g, " $1").replace(/([0-9])/g, " $1")}
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type={key.includes("transparent") ? "text" : "color"}
                    name={key}
                    value={value}
                    onChange={handleInputChange}
                    className={`w-full h-10 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent ${
                      key.includes("transparent") ? "px-3" : "px-1"
                    }`}
                  />
                  <span className="text-sm text-gray-600 font-mono min-w-[120px] bg-white px-3 py-2 rounded">{value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex justify-end">
        <button
          onClick={handleChangeTheme}
          disabled={isLoading}
          className={`px-6 py-3 rounded-lg text-white font-medium transition-all transform hover:scale-105
            ${isLoading 
              ? "bg-gray-400 cursor-not-allowed" 
              : "bg-primary hover:bg-opacity-90 shadow-lg hover:shadow-xl"
            }`}
        >
          {isLoading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating...
            </span>
          ) : (
            "Apply Theme"
          )}
        </button>
      </div>
    </div>
  );
}

export default Test;